import { getIdToken } from "../auth";
import { registerWaveBlobAttachment } from "./waveBlobUpload";

let ragSearchUrl: string | null = null;

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;

export function setRagSearchUrl(url: string | undefined): void {
  const trimmed = normalizeRagApiBase(url?.trim() ?? "");
  ragSearchUrl = trimmed || null;
}

function normalizeRagApiBase(raw: string): string {
  let base = raw.replace(/\/+$/, "");
  for (const suffix of [
    "/rag/search",
    "/rag/list",
    "/rag/index-status",
    "/rag/index-wave",
    "/rag/index",
  ]) {
    if (base.endsWith(suffix)) {
      base = base.slice(0, -suffix.length).replace(/\/+$/, "");
    }
  }
  return base;
}

function ragBase(): string {
  if (!ragSearchUrl) {
    throw new Error(
      "RAG non configurato (rag_search_url). Verifica client-config cloud.",
    );
  }
  return ragSearchUrl;
}

/** In dev, Vite proxies `/rag/*` to VITE_RAG_SEARCH_URL (same-origin, no CORS). */
function useDevRagProxy(): boolean {
  if (!import.meta.env.DEV) return false;
  const raw = import.meta.env as Record<string, string | undefined>;
  if (raw.VITE_RAG_DEV_PROXY === "0" || raw.VITE_RAG_DEV_PROXY === "false") {
    return false;
  }
  return Boolean(raw.VITE_RAG_SEARCH_URL?.trim());
}

function buildRagRequestUrl(path: string): string {
  if (useDevRagProxy()) return path;
  return `${ragBase()}${path}`;
}

function parseRagErrorBody(text: string, status: number, path: string): string {
  try {
    const json = JSON.parse(text) as { error?: string; message?: string };
    if (json.error?.trim()) return json.error.trim();
    if (json.message?.trim()) return json.message.trim();
  } catch {
    /* use raw text */
  }
  const snippet = text.trim().slice(0, 300);
  return snippet || `RAG ${path} (HTTP ${status})`;
}

async function ragPost<T>(path: string, body: Record<string, unknown>): Promise<T> {
  const token = await getIdToken();
  const url = buildRagRequestUrl(path);
  let res: Response;
  try {
    res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-oidc-token": token,
      },
      body: JSON.stringify(body),
    });
  } catch (e) {
    if (e instanceof TypeError) {
      const target = useDevRagProxy() ? `proxy → ${ragSearchUrl ?? "?"}` : url;
      throw new Error(
        `Impossibile contattare il servizio RAG (rete o CORS) su ${target}. ` +
          "Verifica rag_search_url, esegui deploy-rag-wave-routes.sh (CORS API Gateway) " +
          "e in dev imposta VITE_RAG_SEARCH_URL in .env.",
      );
    }
    throw e;
  }
  const text = await res.text();
  if (!res.ok) {
    const hint =
      res.status === 404 &&
      (path === "/rag/index-wave" || path === "/rag/index-status")
        ? " Route non deployata: esegui terraform apply in cloud/terraform-rag."
        : "";
    throw new Error(parseRagErrorBody(text, res.status, path) + hint);
  }
  try {
    return JSON.parse(text) as T;
  } catch {
    throw new Error(`RAG ${path}: risposta non valida`);
  }
}

export type WaveRagChunk = {
  text: string;
  contextBefore?: string | null;
  contextAfter?: string | null;
  fileName?: string;
  s3Key?: string;
  score?: number;
};

export type WaveIndexResult = {
  indexed: boolean;
  chunks?: number;
  reason?: string | null;
  queued?: boolean;
};

export type WaveIndexStatus = {
  ragStatus: string;
  chunks: number;
  reason: string | null;
};

/** Files larger than this with a single RAG chunk are likely degraded. */
export const WAVE_RAG_SINGLE_CHUNK_WARN_BYTES = 20 * 1024;

export function waveRagIndexedBadgeLabel(chunks?: number): string {
  if (chunks != null && chunks > 0) {
    return `Indicizzato · ${chunks} chunk`;
  }
  return "Indicizzato";
}

export function waveRagIndexedBadgeTitle(
  chunks?: number,
  reason?: string | null,
): string | undefined {
  const parts: string[] = [];
  if (chunks != null && chunks > 0) parts.push(`${chunks} chunk indicizzati`);
  if (reason?.trim()) parts.push(reason.trim());
  return parts.length ? parts.join(" · ") : undefined;
}

export function isWaveRagSingleChunkDegraded(
  chunks: number | undefined,
  fileSize: number | undefined,
  reason?: string | null,
): boolean {
  if (reason === "single_chunk_suspect") return true;
  const size = fileSize ?? 0;
  return chunks === 1 && size > WAVE_RAG_SINGLE_CHUNK_WARN_BYTES;
}

/** Register uploaded attachment so wave participants can download via presigned URL. */
export async function registerWaveAttachment(
  waveId: string,
  s3Key: string,
  fileName: string,
): Promise<void> {
  await registerWaveBlobAttachment(waveId, s3Key, fileName);
}

function indexFailureMessage(reason?: string): string {
  switch (reason) {
    case "no_text_extracted":
      return "Nessun testo estraibile da questo file.";
    default:
      return reason?.trim() || "Indicizzazione non riuscita.";
  }
}

/** Poll server-side ragStatus until indexed, error, or timeout. */
export async function getWaveIndexStatus(
  waveId: string,
  s3Key: string,
): Promise<WaveIndexStatus> {
  const result = await ragPost<WaveIndexStatus>("/rag/index-status", {
    scope: "wave",
    waveId,
    s3Key,
  });
  return {
    ragStatus: result.ragStatus ?? "none",
    chunks: result.chunks ?? 0,
    reason: result.reason ?? null,
  };
}

export async function pollWaveIndexStatus(
  waveId: string,
  s3Key: string,
  options?: {
    intervalMs?: number;
    timeoutMs?: number;
    /** Notifica gli stati intermedi (es. "indexing") durante il poll. */
    onStatus?: (ragStatus: string) => void;
  },
): Promise<WaveIndexResult> {
  const intervalMs = options?.intervalMs ?? POLL_INTERVAL_MS;
  const timeoutMs = options?.timeoutMs ?? POLL_TIMEOUT_MS;
  const deadline = Date.now() + timeoutMs;

  while (Date.now() < deadline) {
    const status = await getWaveIndexStatus(waveId, s3Key);
    if (status.ragStatus === "indexed") {
      return { indexed: true, chunks: status.chunks, reason: status.reason };
    }
    if (status.ragStatus === "error") {
      throw new Error(indexFailureMessage(status.reason ?? undefined));
    }
    options?.onStatus?.(status.ragStatus);
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  throw new Error("Indicizzazione in timeout. Riprova tra qualche minuto.");
}

/** Queue indexing on shared pdf-ingest worker, then poll until complete. */
export async function indexWaveDocument(
  waveId: string,
  s3Key: string,
  fileName: string,
  mimeType?: string,
  scope?: { spaceId?: string; blockId?: string },
): Promise<WaveIndexResult> {
  try {
    await registerWaveAttachment(waveId, s3Key, fileName);
  } catch (e) {
    // Best-effort: /rag/index-wave registers the attachment authoritatively.
    console.warn(
      "Wave attachment pre-register failed (continuing with index-wave):",
      e instanceof Error ? e.message : e,
    );
  }
  const result = await ragPost<WaveIndexResult & { ragStatus?: string }>(
    "/rag/index-wave",
    {
      waveId,
      s3Key,
      fileName,
      mimeType: mimeType ?? "",
      spaceId: scope?.spaceId ?? "",
      blockId: scope?.blockId ?? "",
    },
  );

  if (result.queued || result.ragStatus === "indexing") {
    return pollWaveIndexStatus(waveId, s3Key);
  }

  if (!result.indexed && result.reason) {
    throw new Error(indexFailureMessage(result.reason));
  }
  return result;
}

/** Hybrid search scoped to a wave thread. Optional s3Key limits to one attachment. */
export async function searchWaveDocuments(
  waveId: string,
  query: string,
  limit = 8,
  s3Key?: string,
): Promise<WaveRagChunk[]> {
  const body: Record<string, unknown> = {
    query,
    scope: "wave",
    scopeId: waveId,
    limit,
  };
  if (s3Key?.trim()) body.s3Key = s3Key.trim();
  const result = await ragPost<{ chunks: WaveRagChunk[] }>("/rag/search", body);
  return result.chunks ?? [];
}
